import { z } from "zod";

export const propelAuthSchema = z.object({
  url: z.string().url(),
});

export const oidcSchema = z.object({
  discoveryUrl: z.string().url(),
  clientId: z.string(),
  issuer: z.string().url().optional(),
  redirectUrl: z.string().url().optional(),
  jwtSecret: z.string().optional(),
  jwtAudience: z.string().default("toolprint-mcp-server"),
});

export const posthogSchema = z.object({
  apiKey: z.string(),
});

export const apiSchema = z.object({
  url: z.string().url(),
});

export const demoSandboxSchema = z.object({
  api: apiSchema,
  invitationCode: z.string(),
});

export const agentSchema = z.object({
  agentType: z.enum(["blaxel", "local"]),
  url: z
    .string()
    .url()
    .transform((url?: string) => {
      if (url !== undefined && url !== null) {
        return url;
      }

      if (process.env.TOOLPRINT_AGENT_URL) {
        return process.env.TOOLPRINT_AGENT_URL;
      }

      return "http://localhost:1338";
    }),
  apiKey: z.string().optional(), // only needed when calling an agent which requires this kind of AuthN (ex. Blaxel)
});

export const appSchema = z.object({
  propelAuth: propelAuthSchema,
  oidc: oidcSchema,
  demoSandbox: demoSandboxSchema,
  agent: agentSchema,
  posthog: posthogSchema.optional(),
});

export const userPreferencesSchema = z.object({
  loginMode: z.enum(["callback", "mcp-gateway"]).default("callback"),
  autoRefreshCredentials: z.boolean().default(true),
});

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  preferences: userPreferencesSchema.default({
    loginMode: "callback",
    autoRefreshCredentials: true,
  }),
});

export const orgSchema = z.object({
  id: z.string(),
});

export const identitySchema = z.object({
  user: userSchema,
  org: orgSchema.optional(),
});

export const credentialsSchema = z.object({
  accessToken: z.string().optional(),
  apiKey: z.string().optional(),
});

export const storedConfigSchema = z.object({
  api: apiSchema,
  identity: identitySchema.optional(),
  credentials: credentialsSchema.optional(),
});

export const configSchema = storedConfigSchema.extend({
  app: appSchema,
});

export type OidcConfig = z.infer<typeof oidcSchema>;
export type PropelAuthConfig = z.infer<typeof propelAuthSchema>;
export type PosthogConfig = z.infer<typeof posthogSchema>;
export type ApiConfig = z.infer<typeof apiSchema>;
export type DemoSandboxConfig = z.infer<typeof demoSandboxSchema>;
export type AppConfig = z.infer<typeof appSchema>;

export type UserPreferencesConfig = z.infer<typeof userPreferencesSchema>;
export type IdentityConfig = z.infer<typeof identitySchema>;
export type CredentialsConfig = z.infer<typeof credentialsSchema>;

export type StoredConfig = z.infer<typeof storedConfigSchema>;

export type Config = z.infer<typeof configSchema>;

function isSensitiveKey(key: string): boolean {
  const sensitiveKeys = ["apiKey", "accessToken"].map((key) =>
    key.toLowerCase().trim(),
  );
  const cleanedKey = key.toLowerCase().trim();
  return sensitiveKeys.includes(cleanedKey);
}

// Custom serializer mode for JSON.stringify
export function maskSensitiveFieldsSerializer(key: string, value: any): any {
  // Omit undefined values
  if (value === undefined) {
    return undefined;
  }
  // Mask sensitive fields
  if (isSensitiveKey(key)) {
    return value ? "***********" : value;
  }
  return value;
}

export function masked(config: Config): string {
  return JSON.stringify(config, maskSensitiveFieldsSerializer);
}
